import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Ship, Wrench, Users, WalletCards } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { getPremiumTranslations } from "@/i18n";
import FleetOperationCard from "@/components/premium-ops/FleetOperationCard";
import { fleetOperationCards } from "@/lib/premiumOperations";

const modules = [
  { icon: Ship, title: "Vessel Analytics", text: "Track readiness, route history, usage, and operating performance across vessels." },
  { icon: Wrench, title: "Maintenance Control", text: "Centralize maintenance logs, due dates, engine-hour triggers, and urgent reminders." },
  { icon: Users, title: "Crew Readiness", text: "Assign roles, prepare checklists, and keep safety responsibilities clear before departure." },
  { icon: WalletCards, title: "Expense Intelligence", text: "Understand fuel, marina, crew, provisioning, and maintenance costs per voyage." }
];

export default function FleetOperations() {
  const { language } = useLanguage();
  const t = getPremiumTranslations(language).fleet;
  const metricTranslations = language === "he" ? ["92%", "בינוני", "$1,240", "מוכן"] : fleetOperationCards.map((card) => card.metric);
  const localizedCards = fleetOperationCards.map((card, index) => ({ ...card, metric: metricTranslations[index], title: t.cards[index], detail: t.cardDetails[index] }));
  const localizedModules = modules.map((module, index) => ({ ...module, title: t.modules[index], text: t.moduleTexts[index] }));

  useEffect(() => {
    document.title = t.meta;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "noindex,nofollow";
  }, [t.meta]);

  return (
    <main className="maritime-page-shell">
      <section className="rounded-3xl border border-border bg-card p-6 shadow-xl md:p-10">
        <p className="text-sm font-semibold uppercase tracking-wider text-primary">{t.eyebrow}</p>
        <h1 className="mt-3 text-balance font-heading text-4xl font-bold md:text-6xl">{t.title}</h1>
        <p className="mt-5 max-w-3xl text-pretty text-base leading-7 text-muted-foreground md:text-lg">
          {t.description}
        </p>
        <Button asChild size="lg" className="mt-8">
          <Link to="/premium">{t.upgrade}</Link>
        </Button>
      </section>

      <section className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {localizedCards.map(card => <FleetOperationCard key={card.title} card={card} />)}
      </section>

      <section className="grid gap-5 md:grid-cols-2">
        {localizedModules.map(module => {
          const Icon = module.icon;
          return (
            <div key={module.title} className="rounded-3xl border border-border bg-card p-6">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon className="h-6 w-6" />
              </div>
              <h2 className="font-heading text-2xl font-bold">{module.title}</h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">{module.text}</p>
            </div>
          );
        })}
      </section>
    </main>
  );
}